import React, { useState, useEffect, useCallback } from 'react';
import GameHUD from '../shared/GameHUD';
import PauseMenu from '../shared/PauseMenu';
import GameComplete from '../GameComplete';
import { calculateRewards } from '../../../services/gameService';
import './TriviaShowdownGame.css';

function TriviaShowdownGame({
    gameContent,
    template,
    topic,
    difficulty = 'intermediate',
    onComplete,
    onQuit
}) {
    // Game state
    const [currentRound, setCurrentRound] = useState(0);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [correctAnswers, setCorrectAnswers] = useState(0);
    const [totalQuestions, setTotalQuestions] = useState(0);
    const [streak, setStreak] = useState(0);
    const [bestStreak, setBestStreak] = useState(0);
    const [lives, setLives] = useState(3);
    const [timeLeft, setTimeLeft] = useState(20);
    const [xpEarned, setXpEarned] = useState(0);
    const [coinsEarned, setCoinsEarned] = useState(0);

    // Question state
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [showResult, setShowResult] = useState(false);
    const [isCorrect, setIsCorrect] = useState(false);

    // UI state
    const [isPaused, setIsPaused] = useState(false);
    const [isComplete, setIsComplete] = useState(false);
    const [victory, setVictory] = useState(false);
    const [showRoundTransition, setShowRoundTransition] = useState(false);
    const [roundTransitionText, setRoundTransitionText] = useState('');

    // Current question
    const [currentQuestion, setCurrentQuestion] = useState(null);

    const handleGameOver = useCallback((won) => {
        setVictory(won);
        setIsComplete(true);

        const accuracy = totalQuestions > 0
            ? Math.round((correctAnswers / totalQuestions) * 100)
            : 0;

        const rewards = calculateRewards(template, correctAnswers, accuracy);

        setXpEarned(rewards.xp);
        setCoinsEarned(rewards.coins);

        if (onComplete) {
            onComplete({
                victory: won,
                score,
                xp: rewards.xp,
                coins: rewards.coins,
                stars: rewards.stars,
                accuracy,
                stats: {
                    questionsAnswered: correctAnswers,
                    bestStreak,
                    roundsCompleted: currentRound + 1
                }
            });
        }
    }, [score, correctAnswers, totalQuestions, bestStreak, currentRound, template, onComplete]);

    const startRound = useCallback((roundIndex) => {
        if (!gameContent?.rounds?.[roundIndex]) {
            // Check for bonus round
            if (gameContent?.bonusRound && roundIndex === gameContent.rounds.length) {
                setShowRoundTransition(true);
                setRoundTransitionText(`⚡ ${gameContent.bonusRound.name}!`);
                setTimeout(() => {
                    setShowRoundTransition(false);
                    setCurrentRound(roundIndex);
                    setCurrentQuestionIndex(0);
                    setCurrentQuestion(gameContent.bonusRound.questions[0]);
                    setTimeLeft(gameContent.bonusRound.questions[0]?.timeLimit || 8);
                }, 2000);
            } else {
                handleGameOver(true);
            }
            return;
        }

        const round = gameContent.rounds[roundIndex];
        setShowRoundTransition(true);
        setRoundTransitionText(`Round ${round.roundNumber}: ${round.name}`);

        setTimeout(() => {
            setShowRoundTransition(false);
            setCurrentRound(roundIndex);
            setCurrentQuestionIndex(0);
            setCurrentQuestion(round.questions[0]);
            setTimeLeft(round.questions[0]?.timeLimit || 20);
        }, 2000);
    }, [gameContent, handleGameOver]);

    const moveToNextQuestion = useCallback(() => {
        setShowResult(false);
        setSelectedAnswer(null);

        const rounds = gameContent.rounds;
        const currentRoundData = currentRound < rounds.length
            ? rounds[currentRound]
            : gameContent.bonusRound;

        const nextQuestionIndex = currentQuestionIndex + 1;

        if (nextQuestionIndex < currentRoundData.questions.length) {
            // More questions in current round
            setCurrentQuestionIndex(nextQuestionIndex);
            setCurrentQuestion(currentRoundData.questions[nextQuestionIndex]);
            setTimeLeft(currentRoundData.questions[nextQuestionIndex]?.timeLimit || 20);
        } else if (currentRound < rounds.length) {
            // Next round (or bonus round)
            startRound(currentRound + 1);
        } else {
            // Game complete
            handleGameOver(true);
        }
    }, [gameContent, currentRound, currentQuestionIndex, startRound, handleGameOver]);

    const handleTimeout = useCallback(() => {
        setShowResult(true);
        setIsCorrect(false);
        setStreak(0);
        setLives(prev => {
            const newLives = prev - 1;
            if (newLives <= 0) {
                setTimeout(() => handleGameOver(false), 1500);
            }
            return newLives;
        });

        setTimeout(() => {
            if (lives > 1) {
                moveToNextQuestion();
            }
        }, 1500);
    }, [lives, handleGameOver, moveToNextQuestion]);

    // Track if game has been initialized
    const [gameInitialized, setGameInitialized] = useState(false);

    // Initialize game - only run once when gameContent is available
    useEffect(() => {
        if (gameContent?.rounds && !gameInitialized) {
            const total = gameContent.rounds.reduce((sum, r) => sum + r.questions.length, 0);
            setTotalQuestions(total);

            // Set difficulty-based settings
            const diffSettings = template?.difficultyScaling?.[difficulty] || {};
            setLives(diffSettings.lives || 3);

            // Mark as initialized and start first round
            setGameInitialized(true);

            // Start first round with transition
            const round = gameContent.rounds[0];
            setShowRoundTransition(true);
            setRoundTransitionText(`Round ${round.roundNumber}: ${round.name}`);

            setTimeout(() => {
                setShowRoundTransition(false);
                setCurrentRound(0);
                setCurrentQuestionIndex(0);
                setCurrentQuestion(round.questions[0]);
                setTimeLeft(round.questions[0]?.timeLimit || 20);
            }, 2000);
        }
    }, [gameContent, template, difficulty, gameInitialized]);

    // Timer
    useEffect(() => {
        if (isPaused || isComplete || showResult || showRoundTransition || !currentQuestion) return;

        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    handleTimeout();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [isPaused, isComplete, showResult, showRoundTransition, currentQuestion, handleTimeout]);

    const getCorrectAnswer = (question) => {
        // Handle case where correctAnswer is a letter index (A, B, C, D) instead of the actual text
        const correctAnswer = question.correctAnswer;
        if (['A', 'B', 'C', 'D'].includes(correctAnswer)) {
            const index = correctAnswer.charCodeAt(0) - 65; // A=0, B=1, C=2, D=3
            return question.options[index] || correctAnswer;
        }
        // Handle numeric index
        if (typeof correctAnswer === 'number' && correctAnswer >= 0 && correctAnswer < question.options.length) {
            return question.options[correctAnswer];
        }
        return correctAnswer;
    };

    const handleAnswer = (answer) => {
        if (showResult || !currentQuestion) return;

        setSelectedAnswer(answer);
        const correctAnswer = getCorrectAnswer(currentQuestion);
        const correct = answer === correctAnswer;
        setIsCorrect(correct);
        setShowResult(true);

        if (correct) {
            const points = currentQuestion.points || 100;
            const streakBonus = streak * 10;
            setScore(prev => prev + points + streakBonus);
            setCorrectAnswers(prev => prev + 1);
            setStreak(prev => {
                const newStreak = prev + 1;
                if (newStreak > bestStreak) setBestStreak(newStreak);
                return newStreak;
            });
            setXpEarned(prev => prev + 15);
        } else {
            setStreak(0);
            setLives(prev => {
                const newLives = prev - 1;
                if (newLives <= 0) {
                    setTimeout(() => handleGameOver(false), 1500);
                }
                return newLives;
            });
        }

        setTimeout(() => {
            if (lives > (correct ? 0 : 1)) {
                moveToNextQuestion();
            }
        }, 1500);
    };

    const handleRestart = () => {
        setCurrentRound(0);
        setCurrentQuestionIndex(0);
        setScore(0);
        setCorrectAnswers(0);
        setStreak(0);
        setBestStreak(0);
        setLives(template?.difficultyScaling?.[difficulty]?.lives || 3);
        setXpEarned(0);
        setCoinsEarned(0);
        setSelectedAnswer(null);
        setShowResult(false);
        setIsPaused(false);
        setIsComplete(false);
        setVictory(false);

        // Restart with round transition
        if (gameContent?.rounds?.[0]) {
            const round = gameContent.rounds[0];
            setShowRoundTransition(true);
            setRoundTransitionText(`Round ${round.roundNumber}: ${round.name}`);

            setTimeout(() => {
                setShowRoundTransition(false);
                setCurrentQuestion(round.questions[0]);
                setTimeLeft(round.questions[0]?.timeLimit || 20);
            }, 2000);
        }
    };

    const getOptionClass = (option) => {
        if (!showResult) {
            return selectedAnswer === option ? 'selected' : '';
        }
        const correctAnswer = currentQuestion ? getCorrectAnswer(currentQuestion) : null;
        if (option === correctAnswer) return 'correct';
        if (option === selectedAnswer && !isCorrect) return 'incorrect';
        return 'dimmed';
    };

    if (!gameContent) {
        return (
            <div className="trivia-loading">
                <div className="loading-spinner">⚡</div>
                <p>Loading trivia...</p>
            </div>
        );
    }

    return (
        <div className="trivia-game">
            <GameHUD
                gameName="Trivia Showdown"
                score={score}
                lives={lives}
                maxLives={template?.difficultyScaling?.[difficulty]?.lives || 3}
                timeLeft={timeLeft}
                combo={streak}
                xp={xpEarned}
                coins={coinsEarned}
                onPause={() => setIsPaused(true)}
            />

            <div className="trivia-info">
                <span className="trivia-topic">📚 {topic}</span>
                <span className="trivia-progress">
                    Question {correctAnswers + (totalQuestions - (gameContent.rounds.reduce((sum, r, i) =>
                        i > currentRound ? sum + r.questions.length :
                        i === currentRound ? sum + (r.questions.length - currentQuestionIndex - 1) : sum, 0) +
                        (gameContent.bonusRound?.questions.length || 0)))} / {totalQuestions}
                </span>
            </div>

            {/* Round Transition */}
            {showRoundTransition && (
                <div className="round-transition">
                    <div className="round-transition-content">
                        <span className="round-icon">🎯</span>
                        <h2>{roundTransitionText}</h2>
                        <p>Get ready!</p>
                    </div>
                </div>
            )}

            {/* Question Area */}
            {!showRoundTransition && currentQuestion && (
                <div className="trivia-question-area">
                    <div className="question-header">
                        <span className="round-indicator">
                            Round {currentRound + 1}
                        </span>
                        {streak > 1 && (
                            <span className="streak-indicator">
                                🔥 {streak} streak!
                            </span>
                        )}
                    </div>

                    <div className="timer-bar">
                        <div
                            className="timer-fill"
                            style={{
                                width: `${(timeLeft / (currentQuestion.timeLimit || 20)) * 100}%`,
                                backgroundColor: timeLeft > 10 ? '#4CAF50' : timeLeft > 5 ? '#ffc107' : '#f44336'
                            }}
                        />
                    </div>

                    <div className="question-card">
                        <h3 className="question-text">{currentQuestion.question}</h3>

                        <div className="options-grid">
                            {currentQuestion.options.map((option, index) => (
                                <button
                                    key={index}
                                    className={`trivia-option ${getOptionClass(option)}`}
                                    onClick={() => handleAnswer(option)}
                                    disabled={showResult}
                                >
                                    <span className="option-letter">
                                        {String.fromCharCode(65 + index)}
                                    </span>
                                    <span className="option-text">{option}</span>
                                    {showResult && option === getCorrectAnswer(currentQuestion) && (
                                        <span className="option-icon">✓</span>
                                    )}
                                    {showResult && option === selectedAnswer && !isCorrect && (
                                        <span className="option-icon">✗</span>
                                    )}
                                </button>
                            ))}
                        </div>

                        {showResult && currentQuestion.explanation && (
                            <div className={`answer-feedback ${isCorrect ? 'correct' : 'incorrect'}`}>
                                <span className="feedback-icon">
                                    {isCorrect ? '🎉' : '📚'}
                                </span>
                                <p>{currentQuestion.explanation}</p>
                            </div>
                        )}
                    </div>

                    <div className="points-display">
                        <span>Points: {currentQuestion.points || 100}</span>
                        {streak > 0 && (
                            <span className="streak-bonus">+{streak * 10} streak bonus!</span>
                        )}
                    </div>
                </div>
            )}

            {/* Pause Menu */}
            {isPaused && (
                <PauseMenu
                    gameName="Trivia Showdown"
                    score={score}
                    stats={{
                        accuracy: totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0,
                        combo: bestStreak
                    }}
                    onResume={() => setIsPaused(false)}
                    onRestart={handleRestart}
                    onQuit={onQuit}
                />
            )}

            {/* Game Complete */}
            {isComplete && (
                <GameComplete
                    victory={victory}
                    gameName="Trivia Showdown"
                    score={score}
                    xpEarned={xpEarned}
                    coinsEarned={coinsEarned}
                    stars={victory ? (correctAnswers === totalQuestions ? 3 : correctAnswers > totalQuestions * 0.7 ? 2 : 1) : 0}
                    accuracy={totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0}
                    stats={{
                        questionsAnswered: correctAnswers,
                        bestStreak
                    }}
                    onPlayAgain={handleRestart}
                    onBackToMap={onQuit}
                    topic={topic}
                />
            )}
        </div>
    );
}

export default TriviaShowdownGame;
